/**
 * Offsets Management Hook
 *
 * This custom hook provides functionality for fetching and managing data offsets
 * for weather station measurements. It uses React Query for data fetching, caching, and pagination.
 *
 * Features:
 * - Data formatting with moment.js
 * - Infinite scrolling support
 * - Automatic caching
 * - Manual refresh capability
 * - Pagination handling
 */

import { useInfiniteQuery, useQueryClient } from "react-query";
import { getFormData } from "./api";
import moment from "moment";

/**
 * Formats an offset object with proper date formatting
 * @param {Object} offset - The raw offset data
 * @returns {Object} Formatted offset data with proper date strings
 */
const formatOffset = (offset) => ({
  id: offset.id,
  field_name: offset.field_name,
  from_date: offset.from_date
    ? moment(offset.from_date).format("MM/DD/YYYY, h:mm:ss a")
    : null,
  to_date: offset.to_date
    ? moment(offset.to_date).format("MM/DD/YYYY, h:mm:ss a")
    : null,
  offset_value: offset.offset_value,
  comment: offset.comment,
  aws_mapping_id: offset.aws_mapping_id,
  uid: offset.uid,
  operator: offset.operator,
  custom_expression: offset.custom_expression,
});

/**
 * Fetches offsets with pagination
 * @param {Object} params - Query parameters
 * @param {number} [params.pageParam=0] - The page number to fetch
 * @returns {Promise<Object>} The response containing formatted offsets and pagination info
 */
const fetchOffset = async ({ pageParam = 0 }) => {
  const response = await getFormData("offset", { page: pageParam, limit: 5 });
  console.log(response);
  return {
    offsets: response.offsets.map(formatOffset),
    nextPage: response.has_more ? pageParam + 1 : undefined,
    totalCount: response.total_count,
  };
};

/**
 * Custom hook for managing data offsets
 * @returns {Object} Query results and utility functions
 * @property {Object} data - The query data
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 * @property {Function} refetchRows - Function to manually refresh the data
 */
export const useOffsets = () => {
  const queryClient = useQueryClient();

  const query = useInfiniteQuery("offset", fetchOffset, {
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: 5 * 60 * 1000, // Data considered fresh for 5 minutes
    cacheTime: 30 * 60 * 1000, // Cache kept for 30 minutes
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnMount: false, // Don't refetch when component mounts
  });

  /**
   * Manually refreshes the offsets data
   * @returns {Promise} A promise that resolves when the data is refreshed
   */
  const refetchRows = async () => {
    await queryClient.resetQueries("offset");
    return query.refetch();
  };

  return { ...query, refetchRows };
};
